// src/WriteView.jsx
// ═══════════════════════════════════════════════════════════════════════════
// Write workbench. On first open, runs a short kickoff questionnaire so
// Aria starts with the right context (what + who + tone + length + topic).
// After the wizard, the user lands on a split view:
//   LEFT  — chat with Aria
//   RIGHT — the draft document (formatted, copy-able)
//
// The questionnaire is intentionally chip-driven — each question has 4–8
// quick-pick options + an "Other..." text fallback — so the user can
// scaffold a writing brief in ~30 seconds.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import './WriteView.css';
import { getApiBase, fetchWithTimeout } from './apiBase';
import { sfx } from './ariaSoundFx';

const API_BASE = getApiBase();

// ─── Kickoff questions ─────────────────────────────────────────────────────
// Each question: key (state field), prompt, chips (quick-picks), optional
// `placeholder` for the topic text field. The wizard walks through these in
// order; the topic is the only required free-text question.
const QUESTIONS = [
  {
    key: 'kind',
    prompt: 'What are you writing?',
    sub: 'Pick the closest match — Aria will adapt.',
    chips: ['Email', 'Letter', 'Essay', 'Story', 'Blog post', 'Cover letter', 'Pitch', 'Speech', 'Caption', 'Poem'],
  },
  {
    key: 'audience',
    prompt: "Who's it for?",
    sub: 'This shapes the language and formality.',
    chips: ['Boss', 'Customer', 'Friend', 'Family', 'Professor', 'Recruiter', 'Investor', 'The public', 'Followers', 'Myself'],
  },
  {
    key: 'tone',
    prompt: 'What tone fits?',
    sub: 'You can switch mid-draft — this is just a starting point.',
    chips: ['Professional', 'Casual', 'Warm', 'Funny', 'Direct', 'Formal', 'Persuasive', 'Apologetic', 'Confident', 'Grateful'],
  },
  {
    key: 'length',
    prompt: 'How long?',
    sub: 'Rough is fine.',
    chips: ['Quick — 1–2 sentences', 'Short — a paragraph', 'Medium — a few paragraphs', 'Long — full piece', 'As long as it needs to be'],
  },
  {
    key: 'topic',
    prompt: "What's it about?",
    sub: 'A sentence or two — what does it need to say? Any names, dates, or key points to include?',
    textOnly: true,
    placeholder: 'e.g. Thank-you note to my manager Sarah for the promotion to senior engineer last week…',
  },
];

export default function WriteView() {
  // Wizard state
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customInput, setCustomInput] = useState('');
  const [wizardDone, setWizardDone] = useState(false);

  // Draft workspace state (post-wizard)
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const chatScrollRef = useRef(null);
  const customInputRef = useRef(null);

  const currentQuestion = QUESTIONS[step];
  const isLastQuestion = step === QUESTIONS.length - 1;

  // Focus the text input when arriving at a text-only step.
  useEffect(() => {
    if (currentQuestion?.textOnly && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [step, currentQuestion]);

  // Auto-scroll chat to bottom on new message.
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const pickChip = (chip) => {
    sfx.click();
    const next = { ...answers, [currentQuestion.key]: chip };
    setAnswers(next);
    if (isLastQuestion) {
      finishWizard(next);
    } else {
      setStep((s) => s + 1);
      setCustomInput('');
    }
  };

  const submitCustom = () => {
    const value = customInput.trim();
    if (!value) return;
    sfx.click();
    const next = { ...answers, [currentQuestion.key]: value };
    setAnswers(next);
    if (isLastQuestion) {
      finishWizard(next);
    } else {
      setStep((s) => s + 1);
      setCustomInput('');
    }
  };

  const goBack = () => {
    if (step === 0) return;
    sfx.click();
    setStep((s) => s - 1);
    setCustomInput('');
  };

  const finishWizard = (finalAnswers) => {
    sfx.complete();
    setWizardDone(true);
    // Greet the user with the brief Aria heard, so they can correct it if
    // she misread anything.
    const summary = `Got it — I'll write a ${finalAnswers.tone?.toLowerCase() || ''} ${finalAnswers.kind?.toLowerCase() || 'piece'} for ${finalAnswers.audience?.toLowerCase() || 'you'}, ${finalAnswers.length?.toLowerCase() || 'as long as it needs to be'}. Drafting now…`.replace(/\s+/g, ' ').trim();
    setMessages([{ role: 'aria', text: summary }]);
    // Auto-fire the first draft so the right pane fills immediately.
    requestDraft(finalAnswers, '');
  };

  // ── Aria call: ask the conversation endpoint to write/edit the draft ────
  const requestDraft = async (brief, userMessage) => {
    setLoading(true);
    const systemPrompt = [
      'You are Aria, an expert writer. The user gave you a brief and is now',
      'iterating on a draft. Each turn, return the FULL UPDATED DRAFT — no',
      'extra commentary, no markdown fences. The draft should be ready to copy',
      'and paste. Match the tone and length they asked for. If the user gives',
      'feedback, rewrite the whole piece with the changes applied — don\'t',
      'just respond conversationally.',
    ].join(' ');

    const briefBlock = [
      'WRITING BRIEF',
      `• Type: ${brief.kind || ''}`,
      `• Audience: ${brief.audience || ''}`,
      `• Tone: ${brief.tone || ''}`,
      `• Length: ${brief.length || ''}`,
      `• Topic / details: ${brief.topic || ''}`,
    ].join('\n');

    const ctx = userMessage
      ? `${briefBlock}\n\nCURRENT DRAFT:\n${draft || '(none yet)'}\n\nUSER FEEDBACK: ${userMessage}\n\nReturn the full revised draft.`
      : `${briefBlock}\n\nReturn the first draft now.`;

    try {
      const res = await fetchWithTimeout(`${API_BASE}/api/ai/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: ctx, systemPrompt, surface: 'write-workbench' }),
        timeout: 60000,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || res.statusText);
      const reply = (data?.reply || data?.text || data?.message || '').trim();
      if (reply) {
        setDraft(reply);
        sfx.complete();
        if (userMessage) {
          setMessages((prev) => [...prev, { role: 'aria', text: 'Updated the draft — anything else?' }]);
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'aria', text: `Couldn't draft that — ${err.message || 'try again'}.`, isError: true }]);
      sfx.error();
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    const text = chatInput.trim();
    if (!text || loading) return;
    sfx.generate();
    setChatInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    requestDraft(answers, text);
  };

  const restartWizard = () => {
    sfx.click();
    setStep(0);
    setAnswers({});
    setCustomInput('');
    setWizardDone(false);
    setMessages([]);
    setDraft('');
  };

  // ─── Render: wizard or workbench ───────────────────────────────────────
  if (!wizardDone) {
    return (
      <div className="write-view">
        <h2 className="write-view__title">Writing</h2>

        <div className="write-wizard">
          {/* Progress dots */}
          <div className="write-wizard__progress" aria-hidden="true">
            {QUESTIONS.map((_, i) => (
              <span
                key={i}
                className={`write-wizard__dot ${i === step ? 'is-current' : ''} ${i < step ? 'is-done' : ''}`}
              />
            ))}
          </div>

          <div className="write-wizard__card">
            <div className="write-wizard__step-label">Step {step + 1} of {QUESTIONS.length}</div>
            <h3 className="write-wizard__prompt">{currentQuestion.prompt}</h3>
            <p className="write-wizard__sub">{currentQuestion.sub}</p>

            {!currentQuestion.textOnly && (
              <div className="write-wizard__chips">
                {currentQuestion.chips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    className="write-wizard__chip"
                    onClick={() => pickChip(chip)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )}

            {/* Free-text fallback for chip questions, OR primary input for the
                final topic question. */}
            <div className="write-wizard__custom-row">
              {currentQuestion.textOnly ? (
                <textarea
                  ref={customInputRef}
                  className="write-wizard__custom-input write-wizard__custom-input--textarea"
                  placeholder={currentQuestion.placeholder}
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  rows={4}
                />
              ) : (
                <input
                  ref={customInputRef}
                  type="text"
                  className="write-wizard__custom-input"
                  placeholder="Something else…"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitCustom(); }}
                />
              )}
              {(customInput.trim() || currentQuestion.textOnly) && (
                <button
                  type="button"
                  className="write-wizard__custom-submit"
                  onClick={submitCustom}
                  disabled={!customInput.trim()}
                >
                  {isLastQuestion ? 'Start writing →' : 'Next →'}
                </button>
              )}
            </div>
          </div>

          <div className="write-wizard__footer">
            {step > 0 && (
              <button type="button" className="write-wizard__back" onClick={goBack}>
                ← Back
              </button>
            )}
            <span className="write-wizard__hint">
              Tip: any answer can be edited mid-draft just by telling Aria.
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-wizard: split chat + draft view ──────────────────────────────
  return (
    <div className="write-view">
      <h2 className="write-view__title">Writing</h2>

      <div className="write-view__brief-bar">
        <span className="write-view__brief-chip">{answers.kind}</span>
        <span className="write-view__brief-dot">·</span>
        <span className="write-view__brief-chip">{answers.audience}</span>
        <span className="write-view__brief-dot">·</span>
        <span className="write-view__brief-chip">{answers.tone}</span>
        <span className="write-view__brief-dot">·</span>
        <span className="write-view__brief-chip">{answers.length}</span>
        <button type="button" className="write-view__edit-brief" onClick={restartWizard}>
          Edit brief
        </button>
      </div>

      <div className="write-view__split">
        {/* LEFT — chat */}
        <section className="write-view__chat">
          <header className="write-view__chat-header">
            <span className="write-view__chat-emoji" aria-hidden="true">✍️</span>
            <div>
              <strong>Aria</strong>
              <span className="write-view__chat-substatus">{loading ? 'drafting…' : 'ready'}</span>
            </div>
          </header>
          <div className="write-view__chat-scroll" ref={chatScrollRef}>
            {messages.map((m, i) => (
              <div key={i} className={`write-view__msg write-view__msg--${m.role} ${m.isError ? 'is-error' : ''}`}>
                <div className="write-view__msg-bubble">{m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="write-view__msg write-view__msg--aria">
                <div className="write-view__msg-bubble write-view__msg-bubble--typing">
                  <span /> <span /> <span />
                </div>
              </div>
            )}
          </div>
          <div className="write-view__input-row">
            <textarea
              className="write-view__input"
              placeholder="Tell Aria what to change…"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={2}
              disabled={loading}
            />
            <button
              type="button"
              className="write-view__send"
              onClick={handleSend}
              disabled={loading || !chatInput.trim()}
            >
              {loading ? '…' : 'Send'}
            </button>
          </div>
        </section>

        {/* RIGHT — draft document */}
        <section className="write-view__draft">
          <header className="write-view__draft-header">
            <span className="write-view__draft-title">Draft</span>
            <button
              type="button"
              className="write-view__draft-copy"
              onClick={async () => {
                sfx.click();
                try { await navigator.clipboard.writeText(draft || ''); } catch (_) {}
              }}
              disabled={!draft}
            >
              Copy
            </button>
          </header>
          <div className="write-view__draft-body">
            {draft ? (
              <pre className="write-view__draft-text">{draft}</pre>
            ) : (
              <div className="write-view__draft-empty">
                {loading ? 'Drafting…' : 'Your draft will appear here.'}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
